'use client';

import { useNavigate, useParams } from 'react-router-dom';
import { useVendorProjects } from '@/hooks/use-projects';
import { MapPin, HandHeart, Loader2, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatNumber } from '@/lib/utils';
import type { ProjectItem } from '@/types';

function formatIndianRupee(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

function ItemsPreview({ items }: { items: ProjectItem[] }) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">No items listed yet.</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex items-start justify-between gap-3 rounded-lg border border-primary/15 bg-primary/5 px-3 py-2.5"
        >
          <div className="min-w-0 flex-1">
            <p className="font-medium">{item.name}</p>
            {item.description ? (
              <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">
                {item.description}
              </p>
            ) : null}
          </div>
          <div className="shrink-0 text-right">
            <p className="font-semibold">
              {formatNumber(item.quantity)}
              {item.unit ? (
                <span className="ml-1 text-sm font-normal text-muted-foreground">{item.unit}</span>
              ) : null}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function ProjectsView() {
  const { communityId } = useParams<{ communityId: string }>();
  const navigate = useNavigate();
  const { projects, isLoading, isError } = useVendorProjects(communityId || '');

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-muted-foreground font-medium animate-pulse">Loading projects...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <p className="text-destructive font-medium">Failed to load projects</p>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <p className="text-muted-foreground font-medium">No projects available at the moment</p>
        <p className="text-sm text-muted-foreground">
          Projects shared with your community will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2">
        <p className="text-muted-foreground max-w-2xl">
          Browse items needed for community projects and submit your proposals.
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        {projects.map((project) => {
          const items = project.items ?? [];
          const previewItems = items.slice(0, 4);
          const remaining = Math.max(0, items.length - previewItems.length);

          return (
            <Card
              key={project.id}
              className="h-full transition-all hover:shadow-md hover:border-primary/30 cursor-pointer overflow-hidden"
              onClick={() => navigate(`/${communityId}/dashboard/projects/${project.id}`)}
            >
              <CardHeader className="pb-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <CardTitle className="text-lg line-clamp-2">{project.name}</CardTitle>
                    {project.community && (
                      <p className="text-sm text-muted-foreground">{project.community.name}</p>
                    )}
                  </div>
                  <Badge variant="secondary" className="shrink-0 gap-1">
                    <Package className="h-3.5 w-3.5" />
                    {items.length || project.itemCount || 0}
                  </Badge>
                </div>
                {(project.location || project.expectedCost != null) && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {project.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{project.location}</span>
                      </div>
                    )}
                    {project.expectedCost != null && (
                      <div className="flex items-center gap-1">
                        <HandHeart className="h-3 w-3" />
                        <span>{formatIndianRupee(project.expectedCost)}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Items required
                </p>
                {previewItems.length > 0 ? (
                  <>
                    <ItemsPreview items={previewItems} />
                    {remaining > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        +{remaining} more item{remaining === 1 ? '' : 's'} — view project
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No items listed yet.</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
